/* eslint-disable no-fallthrough */
import { ApplicantStatus, ApplicationStatus } from "shared/types";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";
import { CheckVerified01, InfoCircle, XClose } from "untitledui-js-base";
import { ReactNode, useMemo } from "react";
import { cx } from "shared/utils/classnames";

interface ApplicationStatusBadge {
  status?: ApplicantStatus | ApplicationStatus;
  size?: BadgeSize;
  className?: string;

  refunded?: boolean;

  // since they both application types use same interface on
  // backend -- we need to diversify statuses.

  // there are some differences on entity level of understanding

  // for applicant status is completed past submitted state
  // also we're not interested in decision on applicant basis

  // for application Not Started and Pending ApplicationStatus translates to Invited.

  // yet another way to put it:
  // when displaying data.application.status -- type: "application"
  // when displaying data.application.applicant[n].["status" | "application_status"] -- type: "applicant"

  // applicant -- status on applicant
  // application --  status of whole application
  type: "applicant" | "application";

  conditions?: string[];
}

const applicationConditionsMap = {
  inadequate_income: "Inadequate income assets",
  unverified_income: "Unverified income assets",
  smoking_habits: "Smoking habits",
  criminal_record_history: "Criminal record history",
  non_permitted_pets: "Non-permitted pets",
  rental_history: "Rental history is lacking",
  history_of_non_payment_or_tardiness: "History of non-payment or tardiness",
  history_of_rental_property_damage: "History of rental property damage",
  application_info_misrepresented: "Application info misrepresented",
  incomplete_application: "Incomplete application",
  credit_background_check_not_authorized:
    "Credit/background check not authorized",
  credit_score_doesnt_meet_renting_criteria:
    "Credit score doesn’t meet renting criteria",
  history_of_bankruptcy: "History of bankruptcy",
  history_of_eviction: "History of housing court proceedings",
  negative_employer_reference: "Negative employer reference",
  disagreement_on_terms_of_rental_agreement:
    "Disagreement on terms of rental agreement",
  registered_sex_offender_record: "Registered sex offender record",
  past_due_accounts: "Past due accounts",
  accounts_in_collections: "Accounts in collections"
};

export const ApplicationStatusBadge = ({
  refunded,
  status,
  type,
  size = BadgeSize.SM,
  className,
  conditions
}: ApplicationStatusBadge) => {
  const {
    variant,
    icon,
    title,
    tooltip
  }: {
    variant: BadgeVariant;
    icon?: ReactNode;
    title: string;
    tooltip?: ReactNode;
  } = useMemo(() => {
    if (!status)
      return {
        variant: BadgeVariant.BASE,
        title: "Invited"
      };

    if (refunded)
      return {
        variant: BadgeVariant.BASE,
        title: "Refunded",
        icon: <XClose size="12" className="stroke-inherit" />
      };

    if (type === "applicant")
      switch (status) {
        case ApplicantStatus.Pending:
          return { variant: BadgeVariant.BASE, title: "Invited" };
        case ApplicantStatus.Finished:
        case ApplicantStatus.Started:
          return {
            variant: BadgeVariant.WARNING,
            title: "In progress"
          };
        case ApplicantStatus.Submitted:
          return {
            variant: BadgeVariant.SUCCESS,
            title: "Submitted"
          };
        case ApplicantStatus.Canceled:
          return {
            variant: BadgeVariant.BASE,
            title: "Canceled"
          };
        case ApplicantStatus.Processing:
          return {
            variant: BadgeVariant.WARNING,
            title: "Waiting for reports"
          };

        default:
          // Should be impossible case
          return {
            variant: BadgeVariant.BASE,
            title: status
          };
      }

    switch (status) {
      case ApplicationStatus.Pending:
        return {
          variant: BadgeVariant.BASE,
          title: "Invited"
        };
      case ApplicationStatus.Started:
        return {
          variant: BadgeVariant.WARNING,
          title: "In progress"
        };
      case ApplicationStatus.Submitted:
        return {
          variant: BadgeVariant.PRIMARY,
          title: "Pending Decision"
        };
      case ApplicationStatus.Approved:
        return {
          icon: <CheckVerified01 size="12" className="stroke-inherit" />,
          title: "Approved",
          variant: BadgeVariant.SUCCESS
        };
      case ApplicationStatus.ConditionalApproved:
        return {
          icon: <InfoCircle size="12" className="stroke-inherit" />,
          title: "Approved",
          tooltip: (
            <>
              Application was conditionally approved
              {conditions?.length ? (
                <ul>
                  {conditions.map((c) => (
                    <li key={c} className="my-1 ml-5 list-disc text-xs">
                      {applicationConditionsMap[c] || c}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ),
          variant: BadgeVariant.WARNING
        };
      case ApplicationStatus.Canceled:
        return {
          icon: <XClose size="12" className="stroke-inherit" />,
          variant: BadgeVariant.BASE,
          title: "Canceled"
        };
      case ApplicationStatus.Rejected:
        return {
          icon: <XClose size="12" className="stroke-inherit" />,
          title: "Declined",
          variant: BadgeVariant.ERROR
        };
      case ApplicationStatus.Merged:
        return {
          variant: BadgeVariant.BASE,
          title: "Merged"
        };
      case ApplicationStatus.Processing:
        return {
          variant: BadgeVariant.WARNING,
          title: "Waiting for reports"
        };
      default:
        return { variant: BadgeVariant.BASE, title: status };
    }
  }, [status, type]);

  return (
    <Badge
      className={cx("shrink-0 items-center", className)}
      size={size}
      variant={variant}
      tooltip={tooltip}
      leftElement={icon}
    >
      {title}
    </Badge>
  );
};
